// src/config/accessCatalog.ts
//
// AUTH-UX-FIRST-1 — the Roles & Access UX projection catalog.
//
// ============================================================
// THIS IS NOT AN AUTHORIZATION SOURCE.
// ============================================================
// It exists so an admin can SEE, in product language, what a configured
// access package means. Every decision that matters is still made by
// Central (capability resolution) and by the CRM server (route
// middleware). Nothing here is consulted at request time, and nothing
// here may ever be used to permit an action.
//
// Two things are recorded per action:
//
//   scope       — the AUTHORITATIVE Central capability key, verified
//                 against Central's `capabilitiesRegistry.ts`. Never
//                 invented: where Central has no key, `scope` is null
//                 and the action is marked accordingly.
//
//   enforcement — how the CRM SERVER actually gates this today, audited
//                 route-by-route in AUTH-ENDTOEND-ACCESS-1. This is the
//                 uncomfortable half, and it is recorded deliberately:
//                 a checkbox that looks enforced but is not is worse
//                 than no checkbox at all.
//
// When a route is migrated to `requireCapabilityMiddleware`, flip its
// entry to 'capability'. The badge, the preview copy and the migration
// report all follow from this one field.

import type { MenuKey } from '../types/api';
import type { CentralRole } from '../services/centralContext';

/**
 * How the CRM server enforces an action TODAY.
 *
 *  capability   — `requireCapabilityMiddleware(domain, action)` gates the
 *                 route. Turning the capability off genuinely denies it.
 *  legacy-role  — `requirePermission(...)` gates it on the workspace ROLE
 *                 only. The capability is configuration; two members with
 *                 different profiles are indistinguishable to the API.
 *  unregistered — Central has no capability key for this area at all, so
 *                 there is nothing to configure yet.
 *  no-route     — Central DEFINES the capability but CRM exposes no
 *                 endpoint that uses it. Nothing to enforce, and nothing at
 *                 risk: kept distinct from `unregistered` so an unbuilt
 *                 feature cannot drag an otherwise-enforced module down to
 *                 "not configurable".
 */
export type EnforcementStatus =
  | 'capability'
  | 'legacy-role'
  | 'unregistered'
  | 'no-route';

export interface CatalogAction {
  /** Stable id within the module. */
  action: string;
  label: string;
  /** Authoritative Central capability key, or null when none exists. */
  scope: string | null;
  enforcement: EnforcementStatus;
  /** What actually gates it server-side — shown in the diagnostic badge. */
  backendNote: string;
  /**
   * FU-7 — workspace roles the SERVER additionally requires, on top of the
   * capability.
   *
   * Present only where a CRM route applies `requirePermission(...)` AND
   * `requireCapabilityMiddleware(...)` together, so granting the capability to
   * someone outside these roles produces a capability they can hold and never
   * use. The editor must not offer such a grant as independently usable.
   *
   * This is DATA because prose in `backendNote` cannot be enforced. The note
   * said "pipelines.* (owner/admin) AND requireCapabilityMiddleware(deals,
   * admin)" all along, and the editor still let an owner tick the box for a
   * member — who then received 403 INSUFFICIENT_ROLE on every attempt.
   *
   * Absent means "the capability alone decides", which is the normal case and
   * the one the product promises. Do not add this field to express a
   * capability that is merely *unenforced* (`legacy-role` with no capability
   * participating at all) — that is a different defect with a different fix,
   * and conflating them would hide it.
   */
  requiresWorkspaceRole?: readonly CentralRole[];
}

export interface CatalogModule {
  key: string;
  label: string;
  /**
   * FU-9 — WHERE THIS LIVES IN CRM, when the editor would otherwise imply a
   * destination that does not exist.
   *
   * The catalog is organised by capability DOMAIN; the admin reads it as a
   * list of places. Most of the time those coincide. Where they do not, the
   * editor silently lies by omission: an owner ticked "Companies" for Zoom
   * Reviewer, looked at the CRM sidebar, found no Companies entry, and
   * reasonably concluded the grant had not worked.
   *
   * It had worked. Companies is the second TAB of the Contacts destination
   * (`sidebarKey: null`, fanned into that row by SIDEBAR_EXTRA_DOMAINS), so
   * there was never a menu entry for the capability to reveal.
   *
   * A sentence in the editor is the whole fix. Presentation only — nothing
   * here is consulted at request time.
   */
  surfaceNote?: string;
  /** The sidebar entry this module drives, when it has one. */
  sidebarKey: MenuKey | null;
  /**
   * Where the module lives when it is not a sidebar item — Settings tabs
   * are gated by `canManageWorkspace`, not by capability.
   */
  surface: 'sidebar' | 'settings-tab' | 'embedded';
  actions: CatalogAction[];
}

const CRM = 'crm';
const k = (domain: string, action: string) => `${CRM}:${domain}:${action}`;

/** Gated by `requireCapabilityMiddleware` — verified in telephony/unibox/emails/outbound/circles. */
const CAP = (note: string): Pick<CatalogAction, 'enforcement' | 'backendNote'> => ({
  enforcement: 'capability',
  backendNote: note,
});
/** Gated by `requirePermission` on workspace role — capability not consulted. */
const ROLE = (note: string): Pick<CatalogAction, 'enforcement' | 'backendNote'> => ({
  enforcement: 'legacy-role',
  backendNote: note,
});
/** No Central capability key exists for this area. */
const NONE = (note: string): Pick<CatalogAction, 'enforcement' | 'backendNote'> => ({
  enforcement: 'unregistered',
  backendNote: note,
});
/** Capability exists in Central; no CRM endpoint consumes it yet. */
const NOROUTE = (note: string): Pick<CatalogAction, 'enforcement' | 'backendNote'> => ({
  enforcement: 'no-route',
  backendNote: note,
});

export const CRM_ACCESS_CATALOG: CatalogModule[] = [
  {
    key: 'contacts',
    label: 'Contacts',
    sidebarKey: 'contacts',
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View', scope: k('contacts', 'read'), ...CAP('requireCapabilityMiddleware(contacts, read)') },
      { action: 'write', label: 'Create & edit', scope: k('contacts', 'write'), ...CAP('requireCapabilityMiddleware(contacts, write)') },
      { action: 'delete', label: 'Delete', scope: k('contacts', 'delete'), ...CAP('requireCapabilityMiddleware(contacts, delete)') },
      { action: 'export', label: 'Export', scope: k('contacts', 'export'), ...NOROUTE('CRM exposes no contact-export endpoint') },
      { action: 'import', label: 'Import', scope: k('contacts', 'import'), ...NOROUTE('CRM exposes no contact-import endpoint') },
    ],
  },
  {
    key: 'companies',
    label: 'Companies',
    // Companies is a TAB inside Contacts, not a top-level entry, so it has no
    // sidebarKey of its own and is fanned into the Contacts row by
    // SIDEBAR_EXTRA_DOMAINS — the same treatment SMS gets under Messages.
    //
    // This reverses the earlier split, deliberately. That split existed to stop
    // `crm:companies:read` revealing CONTACTS while the two were separate
    // destinations. They are now ONE destination, so the row it reveals is the
    // one that actually contains Companies; what must not leak is the CONTACTS
    // DATA, and that is enforced at the tab, which renders only with
    // `crm:contacts:read`. Revealing a shell whose only readable tab is
    // Companies grants nothing extra.
    sidebarKey: null,
    surface: 'sidebar',
    surfaceNote:
      'Appears as the Companies tab inside Contacts — there is no separate Companies menu entry.',
    actions: [
      { action: 'read', label: 'View', scope: k('companies', 'read'), ...CAP('requireCapabilityMiddleware(companies, read)') },
      { action: 'write', label: 'Create & edit', scope: k('companies', 'write'), ...CAP('requireCapabilityMiddleware(companies, write)') },
      { action: 'delete', label: 'Delete', scope: k('companies', 'delete'), ...CAP('requireCapabilityMiddleware(companies, delete)') },
    ],
  },
  {
    key: 'deals',
    label: 'Deals',
    sidebarKey: 'pipeline',
    surface: 'sidebar',
    actions: [
      // Central's registry says `deals`; the CRM routes are named
      // `opportunities`/`pipelines`. The drift is recorded, not papered over.
      { action: 'read', label: 'View', scope: k('deals', 'read'), ...CAP('requireCapabilityMiddleware(deals, read)') },
      { action: 'write', label: 'Create & edit', scope: k('deals', 'write'), ...CAP('requireCapabilityMiddleware(deals, write)') },
      { action: 'delete', label: 'Delete', scope: k('deals', 'delete'), ...CAP('requireCapabilityMiddleware(deals, delete)') },
      // Pipeline/stage definition is workspace configuration, so it keeps
      // the owner/admin floor AND requires the capability. Because the role
      // gate still participates, this action is not "capability enforced".
      // THE ONE DUAL-GATED ACTION IN THE CATALOG. routes/pipelines.ts applies
      // the owner/admin role floor AND the capability, deliberately: a
      // pipeline defines the sales process for the entire workspace, so
      // dropping the floor would let any member holding `crm:deals:admin`
      // redefine everyone's. Owner decision, taken and recorded.
      //
      // `requiresWorkspaceRole` is what stops the editor offering it to a
      // member as though it would work.
      {
        action: 'admin',
        label: 'Manage pipelines & stages',
        scope: k('deals', 'admin'),
        requiresWorkspaceRole: ['owner', 'admin'],
        ...ROLE('pipelines.* (owner/admin) AND requireCapabilityMiddleware(deals, admin)'),
      },
    ],
  },
  {
    key: 'tasks',
    label: 'Tasks',
    sidebarKey: 'tasks',
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View', scope: k('tasks', 'read'), ...CAP('requireCapabilityMiddleware(tasks, read)') },
      { action: 'write', label: 'Create & edit', scope: k('tasks', 'write'), ...CAP('requireCapabilityMiddleware(tasks, write)') },
      { action: 'delete', label: 'Delete', scope: k('tasks', 'delete'), ...CAP('requireCapabilityMiddleware(tasks, delete)') },
    ],
  },
  {
    // Central gained a first-class `crm:messages` domain with the Unibox
    // work (ADR-0023), and both the messaging routes and the live rail were
    // migrated onto it. It is the Messages ENTRY capability; `conversations`
    // and `sms` are contained by the row but deliberately do not reveal it —
    // see `SIDEBAR_ENTRY_SCOPES`.
    key: 'messages',
    label: 'Messages',
    sidebarKey: 'messages',
    surface: 'sidebar',
    actions: [
      // Central DEFINES crm:messages:* (ADR-0023) and the live rail gates the
      // menu on `read` — but no CRM route consumes the domain: the messaging
      // work enforced sms:send / email:send and thread scoping instead. So
      // this is menu-level only, and saying otherwise would claim a hidden
      // menu blocks the API when it does not.
      { action: 'read', label: 'View', scope: k('messages', 'read'), ...NOROUTE('menu visibility only — no CRM route consumes crm:messages') },
      { action: 'write', label: 'Send & reply', scope: k('messages', 'write'), ...NOROUTE('no CRM route consumes crm:messages:write') },
      { action: 'admin', label: 'Manage', scope: k('messages', 'admin'), ...NOROUTE('no CRM route consumes crm:messages:admin') },
    ],
  },
  {
    key: 'conversations',
    label: 'Conversations',
    surfaceNote:
      'Reached from inside Messages — no separate Conversations menu entry.',
    // Not its own sidebar row: it is fanned into Messages via
    // SIDEBAR_EXTRA_DOMAINS so its actions stay grantable without it
    // becoming a second way to reveal the menu.
    sidebarKey: null,
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View', scope: k('conversations', 'read'), ...ROLE('assistant.use') },
      { action: 'write', label: 'Reply', scope: k('conversations', 'write'), ...ROLE('assistant.use') },
      { action: 'delete', label: 'Delete', scope: k('conversations', 'delete'), ...ROLE('assistant.use') },
    ],
  },
  {
    key: 'phone',
    label: 'Phone',
    sidebarKey: 'phone',
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View call history', scope: k('phone', 'read'), ...CAP('requireCapabilityMiddleware(phone, read)') },
      { action: 'call', label: 'Place calls', scope: k('phone', 'call'), ...CAP('requireCapabilityMiddleware(phone, call)') },
      { action: 'admin', label: 'Administer telephony', scope: k('phone', 'admin'), ...ROLE('telephony.* — owner/admin') },
    ],
  },
  {
    key: 'sms',
    label: 'SMS',
    surfaceNote:
      'Reached from inside Messages — SMS is a medium there, not a separate menu entry.',
    sidebarKey: null,
    surface: 'embedded',
    actions: [
      { action: 'read', label: 'View messages', scope: k('sms', 'read'), ...CAP('requireCapabilityMiddleware(sms, read)') },
      { action: 'send', label: 'Send SMS', scope: k('sms', 'send'), ...CAP('requireCapabilityMiddleware(sms, send)') },
      { action: 'admin', label: 'Administer SMS', scope: k('sms', 'admin'), ...ROLE('sms.* — owner/admin') },
    ],
  },
  {
    key: 'email',
    label: 'Email',
    surfaceNote:
      'Reached from inside Messages — email is a medium there, not a separate menu entry.',
    // Email is a MEDIUM inside Messages, not a destination. The "Emails" entry
    // pointed at `/messages?medium=email` — the same page Messages points at,
    // guarded by the same `crm:messages:read` route check — so it was a second
    // link to one surface, and the reason both entries lit up at once.
    //
    // Fanned into the Messages row (SIDEBAR_EXTRA_DOMAINS) so `crm:email:*`
    // stays grantable where the admin looks for it. It deliberately does NOT
    // join SIDEBAR_ENTRY_SCOPES: `crm:email:read` never opened this page —
    // EmailsPage refuses to render without `crm:messages:read` — so letting it
    // reveal the entry would restore a link that only ever reaches
    // "Messages unavailable".
    sidebarKey: null,
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View email', scope: k('email', 'read'), ...ROLE('unibox.* — role gate only') },
      { action: 'send', label: 'Send email', scope: k('email', 'send'), ...CAP('requireCapabilityMiddleware(email, send)') },
      { action: 'admin', label: 'Administer email', scope: k('email', 'admin'), ...ROLE('unibox.* — owner/admin') },
    ],
  },
  {
    key: 'automations',
    label: 'Campaigns & Automations',
    sidebarKey: 'channels',
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View', scope: k('automations', 'read'), ...CAP('requireCapabilityMiddleware(automations, read)') },
      { action: 'write', label: 'Create & edit', scope: k('automations', 'write'), ...CAP('requireCapabilityMiddleware(automations, write)') },
      { action: 'execute', label: 'Run', scope: k('automations', 'execute'), ...CAP('requireCapabilityMiddleware(automations, execute)') },
      { action: 'delete', label: 'Delete', scope: k('automations', 'delete'), ...CAP('requireCapabilityMiddleware(automations, delete)') },
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    surfaceNote:
      'Embedded in other surfaces — no menu entry of its own.',
    sidebarKey: null,
    surface: 'embedded',
    actions: [
      // `events.*` is the CALENDAR domain, not analytics — the previous
      // note conflated them. Analytics reads map to `audit.view` only.
      // Unresolved: the audit trail moved to `crm:audit:read`, and no CRM
      // route maps defensibly to `crm:analytics:read` yet.
      { action: 'read', label: 'View reports', scope: k('analytics', 'read'), ...ROLE('no defensible route mapping yet') },
      // The key exists in Central; CRM exposes no analytics-export endpoint.
      { action: 'export', label: 'Export', scope: k('analytics', 'export'), ...NOROUTE('CRM exposes no analytics-export endpoint') },
    ],
  },
  {
    // The audit trail is its own domain — deliberately NOT folded into
    // Analytics, which remains unmapped.
    key: 'audit',
    label: 'Audit Log',
    surfaceNote:
      'A tab inside Settings — no separate Audit Log menu entry.',
    sidebarKey: null,
    surface: 'settings-tab',
    actions: [
      { action: 'read', label: 'View audit log', scope: k('audit', 'read'), ...CAP('requireCapabilityMiddleware(audit, read)') },
    ],
  },
  {
    key: 'ai',
    label: 'AI Assistant',
    surfaceNote:
      'Embedded in other surfaces — no menu entry of its own.',
    sidebarKey: null,
    surface: 'embedded',
    actions: [
      { action: 'read', label: 'View', scope: k('ai', 'read'), ...ROLE('assistant.use') },
      { action: 'use', label: 'Use assistant', scope: k('ai', 'use'), ...ROLE('assistant.use') },
      { action: 'admin', label: 'Administer AI', scope: k('ai', 'admin'), ...ROLE('ai_memory.* — owner/admin') },
    ],
  },
  {
    // Gained `crm:projects` in the ADR-0023 wave. Previously uncontrolled.
    key: 'projects',
    label: 'Projects',
    sidebarKey: 'projects',
    surface: 'sidebar',
    actions: [
      // The Projects work gated the UI controls on capabilities; the ROUTES
      // are still requirePermission('projects.*'). Registry exists != route
      // enforced.
      { action: 'read', label: 'View', scope: k('projects', 'read'), ...ROLE("requirePermission('projects.read')") },
      { action: 'write', label: 'Create & edit', scope: k('projects', 'write'), ...ROLE("requirePermission('projects.create')") },
      { action: 'delete', label: 'Delete', scope: k('projects', 'delete'), ...ROLE("requirePermission('projects.delete')") },
    ],
  },
  // --------------------------------------------------------------------
  // AREAS THAT USED TO HAVE NO CAPABILITY
  //
  // These eleven sidebar entries had no Central key, so the editor listed
  // them as "Not access-controlled yet" and every member saw them however
  // the admin configured access. Central now publishes a domain for each,
  // so the toggle is real and per-user.
  //
  // ENFORCEMENT IS REPORTED HONESTLY. Granting these changes what the
  // NAVIGATION shows; it does not close the API. Most of the routes behind
  // them still carry only `requireHybridAuth` — authenticated, but not
  // authorized — and Meetups/Schedule carry a legacy requirePermission.
  //
  // Files is no longer among them: C4.5 R3 migrated read, write and delete to
  // Central capabilities, so all three are marked 'capability' because they
  // genuinely are.
  // Marking them 'capability' would claim a protection that does not exist,
  // which is worse than the gap itself.
  // --------------------------------------------------------------------
  // Meetups consolidated into Schedule (Phase C): a Meetup is an Event
  // kind='meetup', managed on the one Schedule surface. No separate nav entry.
  {
    key: 'schedule',
    label: 'Schedule',
    sidebarKey: 'schedule',
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View', scope: k('schedule', 'read'), ...ROLE("requirePermission('events.*') on /api/events") },
      { action: 'write', label: 'Create & edit', scope: k('schedule', 'write'), ...ROLE("requirePermission('events.*') on /api/events") },
      { action: 'delete', label: 'Delete', scope: k('schedule', 'delete'), ...ROLE("requirePermission('events.*') on /api/events") },
    ],
  },
  {
    key: 'referrals',
    label: 'Referrals',
    sidebarKey: 'referrals',
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View', scope: k('referrals', 'read'), ...ROLE("requirePermission('referrals.view') on /api/referrals") },
      { action: 'write', label: 'Create & edit', scope: k('referrals', 'write'), ...ROLE("requirePermission('referrals.create') on /api/referrals") },
      { action: 'delete', label: 'Delete', scope: k('referrals', 'delete'), ...ROLE("requirePermission('referrals.create') on /api/referrals") },
    ],
  },
  {
    key: 'forms',
    label: 'Forms',
    sidebarKey: 'forms',
    surface: 'sidebar',
    actions: [
      // POST /api/forms/:id/submissions stays deliberately public — anonymous
      // intake is how respondents answer a form. It is not a gap.
      { action: 'read', label: 'View', scope: k('forms', 'read'), ...ROLE("requirePermission('forms.read') on /api/forms") },
      { action: 'write', label: 'Create & edit', scope: k('forms', 'write'), ...ROLE("requirePermission('forms.create'|'forms.manage') on /api/forms") },
      { action: 'delete', label: 'Delete', scope: k('forms', 'delete'), ...ROLE("requirePermission('forms.manage') on /api/forms") },
    ],
  },
  {
    key: 'files',
    label: 'Files',
    sidebarKey: 'files',
    surface: 'sidebar',
    actions: [
      // ALL THREE are genuinely capability-enforced as of C4.5 R3. Every one
      // of them previously carried a CRM role check that never consulted
      // capabilities.crm.files, which an external principal satisfied by
      // construction (it carries role=member).
      //
      // Each is enforced at the route AND at the second place that route gates
      // cannot reach:
      //
      //   read    resolveFileReadAuthority — covers the outbound-message
      //           attachment path, embeds, search, index and sync, whose
      //           routes gate on their own domains, never on Files.
      //   write   syncService.upsertFile — POST /sync/upload is multi-domain
      //   delete  and carries no capability middleware of its own.
      //
      // GET /files/upload-url counts as WRITE: it mints a presigned S3 PUT.
      { action: 'read', label: 'View', scope: k('files', 'read'), ...CAP("requireCapabilityMiddleware('files','read') + resolveFileReadAuthority") },
      { action: 'write', label: 'Create & edit', scope: k('files', 'write'), ...CAP("requireCapabilityMiddleware('files','write') + syncService.upsertFile") },
      { action: 'delete', label: 'Delete', scope: k('files', 'delete'), ...CAP("requireCapabilityMiddleware('files','delete') + syncService.upsertFile") },
    ],
  },
  {
    key: 'offerings',
    label: 'Offerings',
    sidebarKey: 'offerings',
    surface: 'sidebar',
    // FU-6. These said `NOROUTE` — "route exists but consumes no capability" —
    // which was true and is no longer. routes/offerings.ts now applies the
    // authority `authorizationMatrix.ts` had declared all along
    // (offerings.read o/a/m/v, offerings.create and offerings.manage o/a).
    //
    // They are `legacy-role`, NOT `capability`: the gate is the workspace
    // ROLE, so ticking `crm:offerings:*` still does not decide anything.
    // Saying `capability` here would be the same lie in a new direction.
    // Moving to capability enforcement needs Central to transcribe this
    // matrix into `rolesAny` first — otherwise read is denied to every
    // member, since no default package grants the scope.
    actions: [
      { action: 'read', label: 'View', scope: k('offerings', 'read'), ...ROLE("requirePermission('offerings.read') — owner/admin/member/viewer") },
      { action: 'write', label: 'Create & edit', scope: k('offerings', 'write'), ...ROLE("requirePermission('offerings.create'|'offerings.manage') — owner/admin") },
      { action: 'delete', label: 'Delete', scope: k('offerings', 'delete'), ...ROLE("requirePermission('offerings.manage') — owner/admin") },
    ],
  },
  {
    key: 'payments',
    label: 'Invoices & Estimates',
    sidebarKey: 'payments',
    surface: 'sidebar',
    // ── FU-4 — THE SCOPE FAMILY CHANGED, AND THE OLD ONE WAS A LIE ─────────
    //
    // These rows used to toggle `crm:payments:read|write|delete`, which read
    // like money authority and enforced NOTHING: no CRM route has ever called
    // `requireCapabilityMiddleware('payments', …)`, so unticking "View" here
    // removed a sidebar row and left `GET /api/payments` fully reachable.
    // Invoice API authority came from the `invoices.*` ROLE matrix, which
    // these checkboxes could not influence in either direction.
    //
    // Phase C made `crm:invoices:*` the real gate, so the rows now toggle the
    // scopes that actually decide. `crm:payments:*` is retained in Central
    // only because memberships carry it; it is deliberately NOT bridged to
    // invoice authority, and nothing in this catalog references it any more.
    //
    // ESTIMATES ARE NOT A SEPARATE ROW. An estimate IS an Invoice with
    // `type: 'estimate'` — one model, one router, one permission family — so a
    // second family would only drift until an estimate became reachable by
    // someone denied the invoice it turns into. The label says both.
    actions: [
      { action: 'read', label: 'View', scope: k('invoices', 'read'), ...CAP('requireCapabilityMiddleware(invoices, read) on /api/payments') },
      { action: 'write', label: 'Create & edit', scope: k('invoices', 'write'), ...CAP('requireCapabilityMiddleware(invoices, write) — create, edit, convert, items, schedules') },
      // Split from `write` because sending is the authority to put a document
      // in front of a customer. Central models `crm:sms:send` and
      // `crm:email:send` the same way. The role sets are identical today, so
      // this grants nobody anything — it buys the ability to withhold sending
      // from someone who may draft.
      //
      // LABELLED "Mark as sent", NOT "Send to customer", because that is what
      // the endpoint does. `sendInvoice` flips `status` from `draft` to
      // `sent` and carries two unbuilt integrations — `// TODO: Stripe` and
      // `// TODO: Email`. Nothing is delivered to anybody. A label promising
      // delivery would make this catalog lie in the same way the old
      // `crm:payments:*` rows did, which is the defect this whole pass exists
      // to remove. When delivery is built the label can change; until then
      // the permission is real and the sending is not.
      {
        action: 'send',
        label: 'Mark as sent',
        scope: k('invoices', 'send'),
        ...CAP(
          'requireCapabilityMiddleware(invoices, send) on POST /api/payments/:id/send — ' +
          'sets status to sent; customer delivery is not implemented',
        ),
      },
      // DUAL-GATED, like `deals:admin` above, and for a comparable reason.
      // The stated intent is member-delete = NO, but under an explicit scope
      // package Central never consults the role baseline — so the scope alone
      // would grant it, and `PATCH /members/:memberId` changes a role without
      // touching scopes (ADR-0023 §12), so a demoted admin would keep it.
      // `requiresWorkspaceRole` is what stops the editor offering this to a
      // member as though it would work.
      {
        action: 'delete',
        label: 'Delete',
        scope: k('invoices', 'delete'),
        requiresWorkspaceRole: ['owner', 'admin'],
        ...CAP("requirePermission('invoices.delete') (owner/admin) AND requireCapabilityMiddleware(invoices, delete)"),
      },
    ],
    // Recording a payment is NOT on this list, deliberately. It is owner-only,
    // enforced in `paymentsController.recordPayment` with `OWNER_REQUIRED`,
    // and no `crm:invoices:*` scope grants it — so offering a toggle would
    // imply a delegation that does not exist.
    surfaceNote:
      'Recording a payment is owner-only and is not configurable here. ' +
      'Estimates are invoices, so they follow these same permissions. ' +
      '"Mark as sent" changes the invoice status — it does not yet deliver ' +
      'anything to the customer.',
  },
  {
    key: 'business_profile',
    label: 'Business Profile',
    sidebarKey: 'businessProfile',
    surface: 'sidebar',
    actions: [
      // FU-6 — as with offerings, the declared matrix is now applied.
      // `business_profile.view` is o/a/m/v; `business_profile.manage` is o/a.
      // Still role-enforced, so still reported as `legacy-role`.
      { action: 'read', label: 'View', scope: k('business_profile', 'read'), ...ROLE("requirePermission('business_profile.view') — owner/admin/member/viewer") },
      { action: 'write', label: 'Create & edit', scope: k('business_profile', 'write'), ...ROLE("requirePermission('business_profile.manage') — owner/admin") },
      // CRM exposes no business-profile delete endpoint at all.
      { action: 'delete', label: 'Delete', scope: k('business_profile', 'delete'), ...NOROUTE('CRM exposes no business-profile delete endpoint') },
    ],
  },
  {
    key: 'portfolio',
    label: 'Portfolio',
    sidebarKey: 'portfolio',
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View', scope: k('portfolio', 'read'), ...ROLE("requirePermission('portfolio.view') on /api/portfolios") },
      { action: 'write', label: 'Create & edit', scope: k('portfolio', 'write'), ...ROLE("requirePermission('portfolio.manage') on /api/portfolios") },
      { action: 'delete', label: 'Delete', scope: k('portfolio', 'delete'), ...ROLE("requirePermission('portfolio.manage') on /api/portfolios") },
    ],
  },
  {
    key: 'recruitment',
    label: 'Recruitment',
    sidebarKey: 'recruitment',
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View', scope: k('recruitment', 'read'), ...ROLE("requirePermission('recruitment.view') on /api/recruitment") },
      { action: 'write', label: 'Create & edit', scope: k('recruitment', 'write'), ...ROLE("requirePermission('recruitment.manage') on /api/recruitment") },
      { action: 'delete', label: 'Delete', scope: k('recruitment', 'delete'), ...ROLE("requirePermission('recruitment.manage') on /api/recruitment") },
    ],
  },
  {
    key: 'marketplace_listings',
    label: 'Marketplace',
    sidebarKey: 'marketplace',
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View', scope: k('marketplace_listings', 'read'), ...NOROUTE('route exists but consumes no capability — requireHybridAuth only') },
      { action: 'write', label: 'Create & edit', scope: k('marketplace_listings', 'write'), ...NOROUTE('route exists but consumes no capability — requireHybridAuth only') },
      { action: 'delete', label: 'Delete', scope: k('marketplace_listings', 'delete'), ...NOROUTE('route exists but consumes no capability — requireHybridAuth only') },
    ],
  },
  {
    key: 'circles',
    label: 'Circles',
    sidebarKey: 'circles',
    surface: 'sidebar',
    actions: [
      // AUTHZ-REGISTRY-1 registered `crm:circles:*`. The routes still use
      // the legacy role gate, so these stay NOT MIGRATED.
      //
      // Circle WRITES will always compose two things: this capability AND
      // an active Head membership on that exact Team. The capability alone
      // never authorises a write to another Team's Circle.
      { action: 'read', label: 'View circles', scope: k('circles', 'read'), ...ROLE('circles.read — owner/admin/member/viewer') },
      { action: 'write', label: 'Create & edit', scope: k('circles', 'write'), ...ROLE('circles.read route gate + Head-of-Team at the controller') },
      { action: 'delete', label: 'Delete', scope: k('circles', 'delete'), ...ROLE('circles.read route gate + Head-of-Team at the controller') },
    ],
  },
  {
    key: 'performance',
    label: 'Performance',
    sidebarKey: 'team',
    surface: 'sidebar',
    actions: [
      // `crm:team:read` already existed — Performance was never actually
      // missing a key, only an accurate mapping.
      { action: 'read', label: 'View performance', scope: k('team', 'read'), ...CAP('requireCapabilityMiddleware(team, read)') },
    ],
  },
  {
    // Was absent from this catalog entirely, so its routes never appeared in
    // the migration backlog. Registered now so they cannot be forgotten.
    key: 'accountability',
    label: 'Work Accountability',
    surfaceNote:
      'A tab inside Settings, shown in the menu under Performance.',
    sidebarKey: null,
    surface: 'settings-tab',
    actions: [
      { action: 'read', label: 'View own policy', scope: k('accountability', 'read'), ...CAP('requireCapabilityMiddleware(accountability, read)') },
      { action: 'manage', label: 'Manage policies', scope: k('accountability', 'manage'), ...CAP('requireCapabilityMiddleware(accountability, manage)') },
      { action: 'assign', label: 'Assign person rules', scope: k('accountability', 'assign'), ...CAP('requireCapabilityMiddleware(accountability, assign)') },
      { action: 'reconcile', label: 'Reconcile sessions', scope: k('accountability', 'reconcile'), ...CAP('requireCapabilityMiddleware(accountability, reconcile)') },
    ],
  },
  {
    key: 'time',
    label: 'Time Tracking',
    surfaceNote:
      'Reached from inside Performance — no separate Time Tracking menu entry.',
    sidebarKey: null,
    surface: 'embedded',
    actions: [
      { action: 'read', label: 'View own time', scope: k('time', 'read'), ...CAP('requireCapabilityMiddleware(time, read)') },
      { action: 'track', label: 'Record & correct time', scope: k('time', 'track'), ...CAP('requireCapabilityMiddleware(time, track)') },
      // No CRM endpoint distinguishes "everyone's time" yet — the legacy
      // `time.view_all` permission has no route of its own.
      { action: 'view_all', label: 'View everyone’s time', scope: k('time', 'view_all'), ...NOROUTE('no CRM endpoint uses time.view_all') },
    ],
  },
  {
    key: 'settings',
    label: 'Workspace Settings',
    sidebarKey: 'settings',
    surface: 'sidebar',
    actions: [
      { action: 'read', label: 'View settings', scope: k('settings', 'read'), ...ROLE('workspaces.* — owner/admin') },
      { action: 'write', label: 'Change settings', scope: k('settings', 'write'), ...ROLE('workspaces.* — owner/admin') },
      { action: 'admin', label: 'Administer workspace', scope: k('settings', 'admin'), ...ROLE('workspaces.* — owner') },
    ],
  },
  {
    key: 'integrations',
    label: 'Integrations',
    surfaceNote:
      'A tab inside Settings — no separate Integrations menu entry.',
    sidebarKey: null,
    surface: 'settings-tab',
    actions: [
      { action: 'read', label: 'View integrations', scope: k('integrations', 'read'), ...ROLE('integrations.view') },
      { action: 'manage', label: 'Manage integrations', scope: k('integrations', 'manage'), ...ROLE('integrations.manage') },
    ],
  },
  {
    key: 'providers',
    label: 'Providers',
    surfaceNote:
      'A tab inside Settings — no separate Providers menu entry.',
    sidebarKey: null,
    surface: 'settings-tab',
    actions: [
      // Provider ADMINISTRATION (credentials/config) is `crm:integrations:*`
      // and is listed under Integrations. What lives here is provider
      // ASSIGNMENT and routing, now registered as
      // `crm:provider_assignments:*`.
      //
      // Neither grants provider USE — that is ProviderAccessGrant, a
      // per-user resource grant, and it is deliberately absent from this
      // catalog because it is not a capability.
      { action: 'read', label: 'View assignments', scope: k('provider_assignments', 'read'), ...ROLE('assignments.view — owner/admin/member/viewer') },
      { action: 'write', label: 'Assign & reassign', scope: k('provider_assignments', 'write'), ...ROLE('assignments.create/modify/reassign/end') },
      { action: 'admin', label: 'Manage provider roles', scope: k('provider_assignments', 'admin'), ...ROLE('provider_roles.manage — owner/admin') },
    ],
  },
  {
    key: 'billing',
    label: 'Billing',
    surfaceNote:
      'A tab inside Settings — no separate Billing menu entry.',
    sidebarKey: null,
    surface: 'settings-tab',
    actions: [
      { action: 'read', label: 'View billing', scope: k('billing', 'read'), ...ROLE('billing.view') },
      { action: 'manage', label: 'Manage billing', scope: k('billing', 'manage'), ...ROLE('billing.manage') },
    ],
  },
  {
    key: 'team',
    label: 'Members & Roles & Access',
    surfaceNote:
      'A tab inside Settings — this is the editor you are using now.',
    sidebarKey: null,
    surface: 'settings-tab',
    actions: [
      { action: 'invite', label: 'Invite members', scope: k('team', 'invite'), ...ROLE('team.invite — owner/admin') },
      { action: 'manage', label: 'Manage members', scope: k('team', 'manage'), ...ROLE('team.manage — owner/admin') },
      { action: 'admin', label: 'Administer roles & access', scope: k('team', 'admin'), ...ROLE('team.manage — owner/admin') },
    ],
  },
];

/** Every capability key the catalog projects, for completeness checks. */
export function catalogScopes(): string[] {
  return CRM_ACCESS_CATALOG.flatMap((m) =>
    m.actions.map((a) => a.scope).filter((s): s is string => !!s),
  );
}

/**
 * Worst-case enforcement for a module — a module is only as enforced as
 * its weakest action, and the badge must not flatter it.
 */
export function moduleEnforcement(m: CatalogModule): EnforcementStatus {
  // Actions with no endpoint are excluded: there is no request to gate, so
  // they are neither enforced nor unenforced. Everything actually exposed
  // still grades to its WEAKEST member — a module is never flattered.
  const exposed = m.actions.filter((a) => a.enforcement !== 'no-route');
  // Every action is defined in Central but exposed by no CRM endpoint. That
  // is 'no-route', NOT 'unregistered' — the two look alike but mean opposite
  // things: 'unregistered' is "Central has no key for this", which is a gap
  // to close, while this is "nothing to gate yet", which is not.
  if (exposed.length === 0) return 'no-route';
  if (exposed.some((a) => a.enforcement === 'unregistered')) return 'unregistered';
  if (exposed.some((a) => a.enforcement === 'legacy-role')) return 'legacy-role';
  return 'capability';
}

/**
 * Sidebar projection rule: a module's sidebar entry shows when the user
 * holds at least one of its ENTRY capabilities. The live sidebar consumes
 * this same rule, so it is no longer merely "expected" —
 * so this describes the target, and the UI labels it as such.
 */
export function expectedSidebarVisible(
  m: CatalogModule,
  granted: ReadonlySet<string>,
): boolean {
  const scoped = m.actions.filter((a) => a.scope);
  if (scoped.length === 0) return true; // unregistered → cannot be predicted
  return scoped.some((a) => granted.has(a.scope!));
}

/**
 * Aggregate enforcement facts for the whole catalog.
 *
 * WHY THIS EXISTS
 * The Modules & Actions panel used to carry a hand-written sentence
 * ("only Phone, SMS and Email are enforced by the CRM server"). That
 * prose was a SECOND source of truth next to `moduleEnforcement`, and it
 * drifted badly: Phone/SMS/Email are in fact only PARTIALLY enforced,
 * while contacts, companies, tasks, automations, audit, performance,
 * accountability and time are fully enforced. The banner therefore
 * contradicted the badges directly beneath it — the one thing this
 * screen must never do, because an admin who believes an action is
 * blocked when it is not will hand out access they think they withheld.
 *
 * Everything here is DERIVED from the same `enforcement` fields the
 * badges read, so the summary cannot drift from them again.
 */
export interface EnforcementSummary {
  /** Actions the server checks on every request. */
  capabilityActions: number;
  /** Actions saved + reported, but still authorized on workspace role. */
  legacyRoleActions: number;
  /** Actions with no CRM endpoint — nothing to gate either way. */
  noRouteActions: number;
  /** Modules where EVERY exposed action is capability-enforced. */
  fullyEnforcedModules: string[];
  /** Modules with a mix of enforced and configuration-only actions. */
  partiallyEnforcedModules: string[];
  /** True when enforcement is not uniform across the catalog. */
  isMixed: boolean;
}

export function enforcementSummary(
  catalog: CatalogModule[] = CRM_ACCESS_CATALOG,
): EnforcementSummary {
  let capabilityActions = 0;
  let legacyRoleActions = 0;
  let noRouteActions = 0;
  const fullyEnforcedModules: string[] = [];
  const partiallyEnforcedModules: string[] = [];

  for (const m of catalog) {
    for (const a of m.actions) {
      if (a.enforcement === 'capability') capabilityActions += 1;
      else if (a.enforcement === 'legacy-role') legacyRoleActions += 1;
      else if (a.enforcement === 'no-route') noRouteActions += 1;
    }
    // Same exclusion rule as `moduleEnforcement`: a no-route action has
    // no request to gate, so it neither earns nor costs enforcement.
    const exposed = m.actions.filter((a) => a.enforcement !== 'no-route');
    if (exposed.length === 0) continue;
    const enforced = exposed.filter((a) => a.enforcement === 'capability').length;
    if (enforced === exposed.length) fullyEnforcedModules.push(m.key);
    else if (enforced > 0) partiallyEnforcedModules.push(m.key);
  }

  return {
    capabilityActions,
    legacyRoleActions,
    noRouteActions,
    fullyEnforcedModules,
    partiallyEnforcedModules,
    isMixed: capabilityActions > 0 && legacyRoleActions > 0,
  };
}

// ---------------------------------------------------------------------
// SIDEBAR-BASED ACCESS
//
// The catalog is organised by CAPABILITY DOMAIN; the admin thinks in
// SIDEBAR ITEMS. These differ in three ways that matter:
//
//   • naming      — `deals` is "Pipeline" in the menu
//   • fan-in      — "Messages" is `conversations` + `sms`;
//                   "Settings" is six administrative domains
//   • coverage    — many menu entries have no capability at all
//
// The view below is derived from the REAL `SIDEBAR_ITEMS`, so it is
// total over the navigation: a menu entry with no capability shows up
// as `uncontrolled` rather than vanishing.
// ---------------------------------------------------------------------

import { SIDEBAR_ITEMS } from '../shell/useStaticShellMenu';

/**
 * Home requires no entitlement. Central says so in its own source:
 * `moduleKeyNormalization.ts` calls it "always available, module key is
 * a no-op", and both `compositeEntitlementResolver` and
 * `subscriptionService` skip it when resolving entitlement.
 */
export const ALWAYS_AVAILABLE_SIDEBAR_KEYS: readonly string[] = ['home'];

/**
 * Actions a row toggle grants.
 *
 * Deliberately the everyday pair only. `delete`, `admin`, `manage`,
 * `export`, `import` and `execute` are destructive, consequential or
 * data-exfiltrating, and are reachable by expanding the row — a
 * convenience switch must not hand out `:admin` as a shortcut.
 */
export const STANDARD_ACTIONS: readonly string[] = ['read', 'write'];

/** How a whole sidebar row is enforced, derived from its actions. */
export type SidebarEnforcement =
  | 'enforced'
  | 'partial'
  | 'ui-only'
  | 'uncontrolled';

export interface SidebarAccessRow {
  key: string;
  label: string;
  /** Capability modules behind this menu entry. Empty = uncontrolled. */
  modules: CatalogModule[];
  /** Every configurable action across those modules. */
  actions: CatalogAction[];
  alwaysAvailable: boolean;
  enforcement: SidebarEnforcement;
}

/**
 * Domains that belong to a menu entry but carry no `sidebarKey` of their
 * own, because they have no menu item — they are reached from inside
 * another one.
 *
 * Explicit and by ID, never by name similarity. Without this, `sms`
 * would be unreachable in the editor even though SMS is sent from
 * Messages, and Performance would hide the two domains it aggregates.
 */
export const SIDEBAR_EXTRA_DOMAINS: Readonly<Record<string, readonly string[]>> = {
  // Companies is the second tab of the Contacts destination; `crm:companies:*`
  // is grantable there rather than under a top-level entry that no longer
  // exists. Tab-level gating keeps the two domains independent — see the
  // `companies` module note.
  contacts: ['companies'],
  // Email joins conversations and SMS: all three are media/contexts reached
  // from inside the one Messages surface, none of them a destination.
  messages: ['conversations', 'sms', 'email'],
  team: ['accountability', 'time'], // the "Performance" menu entry
};

/** Sidebar row → the capability modules behind it. Never name-matched. */
function modulesForSidebar(menuKey: string): CatalogModule[] {
  const extras = SIDEBAR_EXTRA_DOMAINS[menuKey] ?? [];
  return CRM_ACCESS_CATALOG.filter(
    (m) => m.sidebarKey === menuKey || extras.includes(m.key),
  );
}

/**
 * The administrative domains that live under Settings. They are NOT a
 * sidebar row each — they are tabs inside one — so they are grouped
 * under it rather than dropped.
 */
export function settingsModules(): CatalogModule[] {
  // A domain explicitly claimed by another menu entry is NOT also a
  // Settings tab here. `accountability` is filed as a settings-tab but
  // is fanned into Performance, and appearing under both rows would mean
  // toggling it in one place silently changed the other.
  const claimed = new Set(
    Object.entries(SIDEBAR_EXTRA_DOMAINS)
      .filter(([menuKey]) => menuKey !== 'settings')
      .flatMap(([, domains]) => domains),
  );
  return CRM_ACCESS_CATALOG.filter(
    (m) =>
      !claimed.has(m.key) &&
      (m.surface === 'settings-tab' || m.sidebarKey === 'settings'),
  );
}

function enforcementFor(
  actions: CatalogAction[],
  alwaysAvailable: boolean,
): SidebarEnforcement {
  if (alwaysAvailable) return 'enforced';
  const configurable = actions.filter((a) => a.scope);
  if (configurable.length === 0) return 'uncontrolled';
  const enforced = configurable.filter((a) => a.enforcement === 'capability');
  if (enforced.length === configurable.length) return 'enforced';
  if (enforced.length === 0) return 'ui-only';
  return 'partial';
}

/**
 * The whole navigation as an access surface, in menu order.
 *
 * Total by construction: it maps over `SIDEBAR_ITEMS`, so nothing in the
 * real menu can be missing from the editor.
 */
export function buildSidebarAccessRows(): SidebarAccessRow[] {
  return SIDEBAR_ITEMS.map(({ menuKey, label }) => {
    const alwaysAvailable = ALWAYS_AVAILABLE_SIDEBAR_KEYS.includes(menuKey);
    // Settings fans in the administrative tabs; every other row is its
    // own module(s).
    const modules = menuKey === 'settings' ? settingsModules() : modulesForSidebar(menuKey);
    const actions = modules.flatMap((m) => m.actions);
    return {
      key: menuKey,
      label,
      modules,
      actions,
      alwaysAvailable,
      enforcement: enforcementFor(actions, alwaysAvailable),
    };
  });
}

/** Scopes a row toggle would grant — standard actions only. */
export function standardScopesFor(row: SidebarAccessRow): string[] {
  return row.actions
    .filter((a) => a.scope && STANDARD_ACTIONS.includes(a.action))
    .map((a) => a.scope!);
}

/** Every configurable scope in a row, for turning it fully off. */
export function allScopesFor(row: SidebarAccessRow): string[] {
  return row.actions.filter((a) => a.scope).map((a) => a.scope!);
}

/** A row counts as "on" when the person holds any of its scopes. */
export function rowIsOn(row: SidebarAccessRow, granted: ReadonlySet<string>): boolean {
  if (row.alwaysAvailable) return true;
  return row.actions.some((a) => a.scope && granted.has(a.scope));
}

// ---------------------------------------------------------------------
// ONE VISIBILITY RULE, THREE CONSUMERS
//
// The access editor, the preview and the member's live sidebar must all
// answer "is this menu entry visible?" the same way. Three independent
// interpretations would drift, and the failure mode is the worst kind:
// the admin switches Pipeline off, the preview agrees, and the member
// still sees Pipeline.
//
// So the rule lives here once and all three import it.
// ---------------------------------------------------------------------

/** How a sidebar entry is governed. Exactly one of three, always. */
export type SidebarControl =
  | 'always-available'
  | 'capability-controlled'
  | 'uncontrolled';

/**
 * Scopes that REVEAL a sidebar row, when that is narrower than everything
 * the row contains.
 *
 * Most rows are revealed by any capability they hold — an admin who grants
 * "Contacts" expects Contacts to appear. Messages is deliberately different:
 * the Unibox work (ADR-0023) made `crm:messages:read` the entry contract and
 * pinned a test asserting the menu does NOT consult `crm:conversations`.
 * `conversations` and `sms` remain grantable under the row because they are
 * real messaging capabilities, but granting one must not reveal a menu the
 * product says is gated on `messages:read`.
 *
 * Keeping that as data here — rather than an `if` in the visibility rule —
 * means the editor, the preview and the live rail cannot disagree about it.
 */
export const SIDEBAR_ENTRY_SCOPES: Readonly<Record<string, readonly string[]>> = {
  messages: ['crm:messages:read'],
};

/** The scopes whose presence makes `row` appear. */
export function entryScopesFor(row: SidebarAccessRow): string[] {
  const explicit = SIDEBAR_ENTRY_SCOPES[row.key];
  if (explicit) return [...explicit];
  return row.actions.filter((a) => a.scope).map((a) => a.scope!);
}

export function sidebarControlFor(row: SidebarAccessRow): SidebarControl {
  if (row.alwaysAvailable) return 'always-available';
  return row.actions.some((a) => a.scope) ? 'capability-controlled' : 'uncontrolled';
}

/**
 * Is this menu entry visible to someone holding `granted`?
 *
 * `always-available`      — yes; Home needs no entitlement.
 * `uncontrolled`          — yes; no capability exists to check, so hiding
 *                           it would be a frontend-only restriction with
 *                           nothing enforcing it. Reported honestly in the
 *                           editor instead.
 * `capability-controlled` — only with at least one of its scopes. The
 *                           workspace role is NOT consulted: Admin is not
 *                           a capability bypass.
 */
export function isSidebarRowVisible(
  row: SidebarAccessRow,
  granted: ReadonlySet<string>,
): boolean {
  switch (sidebarControlFor(row)) {
    case 'always-available':
    case 'uncontrolled':
      return true;
    case 'capability-controlled':
      // Entry scopes, not every scope the row contains — see
      // SIDEBAR_ENTRY_SCOPES.
      return entryScopesFor(row).some((scope) => granted.has(scope));
  }
}

/** The menu keys a holder of `granted` should see, in menu order. */
export function visibleSidebarKeys(granted: ReadonlySet<string>): string[] {
  return buildSidebarAccessRows()
    .filter((r) => isSidebarRowVisible(r, granted))
    .map((r) => r.key);
}

/**
 * Flatten Central's nested capability map into the scope strings the rest
 * of this module speaks.
 *
 * Shape is `capabilities[appId][domain][action] === true`; only `true`
 * counts, so an explicitly-false or absent entry is never a grant.
 */
export function scopesFromCapabilities(
  capabilities: unknown,
  appId = 'crm',
): Set<string> {
  const out = new Set<string>();
  const app = (capabilities as Record<string, unknown> | undefined)?.[appId];
  if (!app || typeof app !== 'object') return out;
  for (const [domain, actions] of Object.entries(app as Record<string, unknown>)) {
    if (!actions || typeof actions !== 'object') continue;
    for (const [action, allowed] of Object.entries(actions as Record<string, unknown>)) {
      if (allowed === true) out.add(`${appId}:${domain}:${action}`);
    }
  }
  return out;
}

// ---------------------------------------------------------------------
// FU-7 — ROLE FLOORS
//
// An action whose server route ALSO requires a workspace role cannot be
// delegated by capability alone. Granting it to someone outside those roles
// stores a capability they can hold and never use, and the product then
// contradicts itself: the editor says yes, the server says
// INSUFFICIENT_ROLE, and the person is sent to an owner to re-grant
// something already ticked.
//
// One rule, so the detailed grid and the sidebar-row grid cannot disagree.
// ---------------------------------------------------------------------

/**
 * Is `action` unusable at `workspaceRole` because of a server role floor?
 *
 * Returns false when the action declares no floor (the normal case) and when
 * the role is not yet known — an unresolved role must not disable a control
 * an owner is entitled to use.
 */
export function roleFloorBlocks(
  action: CatalogAction,
  workspaceRole: CentralRole | null | undefined,
): boolean {
  const floor = action.requiresWorkspaceRole;
  if (!floor || floor.length === 0) return false;
  if (!workspaceRole) return false;
  return !floor.includes(workspaceRole);
}

/** Why the control is unavailable, in product language. */
export function roleFloorReason(action: CatalogAction): string | null {
  const floor = action.requiresWorkspaceRole;
  if (!floor || floor.length === 0) return null;
  const names = floor.map((r) => r.charAt(0).toUpperCase() + r.slice(1));
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
  return `Requires the ${list} workspace role. Granting this on its own has no effect at other roles.`;
}
